import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getOrganizationSubscriptionHistory } from "../../../common/services/marketdata/organizationSubscriptionService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const OrganizationSubscriptionHistory = ({ organizationSubscriptionId }) => {
  const { t } = useTranslation(["auth"]);
  const [state, setState] = useState({
    organizationSubscriptionHistory: [],
    isLoading: true,
  });

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 100,
    }),
    []
  );

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      { field: "id", headerName: t("RecordID") },
      { field: "organizationName", headerName: t("OrganizationName") },
      { field: "subscribableName", headerName: t("SubscribableName") },
      {
        field: "compensated",
        headerName: t("Compensated"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.compensatedString,
        cellRenderer: (params) => {
          const isActive = params.data?.compensatedString;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      { field: "updatedBy", headerName: t("UpdatedBy") },
      { field: "updatedDate", headerName: t("UpdateDate") },
      { field: "writeDate", headerName: t("WriteDate") },
      { field: "eventId", headerName: t("EventID") },
    ],
    [t]
  );

  // Memoize grid options for optimal rendering
  const gridOptions = useMemo(
    () => ({
      pagination: true,
      paginationPageSize: 20,
      domLayout: "autoHeight",
    }),
    []
  );

  // Map subscription data to view model format
  const mapToViewModel = useCallback((organizationSubscriptions) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return organizationSubscriptions.map((organizationSubscription) => ({
      id: organizationSubscription.id,
      organizationName: organizationSubscription.organizationName,
      subscribableName: organizationSubscription.subscribableName,
      compensated: organizationSubscription.compensated,
      active: organizationSubscription.active,
      updatedByUserAccountId: organizationSubscription.updatedByUserAccountId,
      updatedBy:
        organizationSubscription.updatedByUserAccountFirstName +
        " " +
        organizationSubscription.updatedByUserAccountLastName,
      updatedDate: new Date(
        organizationSubscription.updatedDate
      ).toLocaleDateString("en-US", dateFormatOptions),
      writeDate: new Date(
        organizationSubscription.writeDate
      ).toLocaleDateString("en-US", dateFormatOptions),
      eventId: organizationSubscription.eventId,
    }));
  }, []);

  // Load subscription history data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const historyData = await getOrganizationSubscriptionHistory(
          organizationSubscriptionId
        );
        const mappedData = mapToViewModel(historyData);

        setState({
          organizationSubscriptionHistory: _.orderBy(mappedData, "id", "desc"),
          isLoading: false,
        });
      } catch (error) {
        console.error(
          "Error fetching organization subscription history:",
          error
        );
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [organizationSubscriptionId, mapToViewModel]);

  const { organizationSubscriptionHistory, isLoading } = state;

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container auth-container">
      {t("OrganizationSubscriptionID")} {organizationSubscriptionId}
      <ThemeableAgGrid
        className="table-extra-small"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={organizationSubscriptionHistory}
        gridOptions={gridOptions}
        getRowId={(params) => params.data.id.toString()}
        animateRows={true}
      />
    </div>
  );
};

export default OrganizationSubscriptionHistory;

